import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaTrash } from "react-icons/fa";
import MDSpinner from "react-md-spinner";
import { toast } from "react-toastify";

import {
  fetchGroupFarms,
  fetchGroupLandPlots,
} from "../PendingSchedules/serviceOrderLocationApi";
import {
  deletePluviometerStation,
  fetchPluviometryMap,
} from "../../utils/farmPluviometryApi";
import FarmRainHeatmapMap from "./FarmRainHeatmapMap";
import FarmMapDeleteConfirmModal from "./FarmMapDeleteConfirmModal";
import PluviometerEditModal from "./PluviometerEditModal";
import PluviometerDayEditModal from "./PluviometerDayEditModal";
import PluviometerRainSheet from "./PluviometerRainSheet";
import { clampSize, useDragResize } from "./useDragResize";
import {
  averageGaugeRainMm,
  buildGaugesFromStations,
  computeRainMapCenter,
  formatRainMm,
  formatRainPeriodLabel,
  resolvePrimaryBoundaryRing,
  resolveQuickRainDate,
  sumGaugeRainMm,
  toDateInputValue,
} from "../../utils/rainHeatmapUtils";
import {
  MAP_VIEWPORT_SCOPES,
  readLocalMapLayout,
  readLocalMapViewport,
  readViewportFromGoogleMap,
  writeLocalMapLayout,
  writeLocalMapViewport,
} from "../../utils/mapViewport";
import MapViewportToolbar from "../../components/MapViewportToolbar";

import "./FarmMapPluviometry.css";

const PERIOD_OPTIONS = [
  { id: "today", label: "Hoje" },
  { id: "week", label: "Última semana" },
  { id: "month", label: "Último mês" },
  { id: "custom", label: "Personalizado" },
];

const DEFAULT_CONTROLS_WIDTH = 320;
const DEFAULT_MAP_HEIGHT = 560;

function readRainMapLayout(farmId) {
  const maxControlsWidth = Math.min(480, window.innerWidth * 0.42);
  const maxMapHeight = Math.min(920, window.innerHeight * 0.78);
  const saved = readLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId);

  const savedControlsWidth =
    saved && saved.listWidth != null ? saved.listWidth : DEFAULT_CONTROLS_WIDTH;
  const savedMapHeight =
    saved && saved.mapHeight != null ? saved.mapHeight : DEFAULT_MAP_HEIGHT;

  return {
    controlsWidth: clampSize(savedControlsWidth, 260, maxControlsWidth),
    mapHeight: clampSize(savedMapHeight, 360, maxMapHeight),
  };
}

function FarmMapPluviometry({ farmId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [targetFarmName, setTargetFarmName] = useState("");
  const [boundaryRing, setBoundaryRing] = useState([]);
  const [landPlots, setLandPlots] = useState([]);
  const [gauges, setGauges] = useState([]);
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState(() => toDateInputValue());
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue());
  const [loadError, setLoadError] = useState("");
  const [sheetGauge, setSheetGauge] = useState(null);
  const [editGauge, setEditGauge] = useState(null);
  const [deleteGauge, setDeleteGauge] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [quickRain, setQuickRain] = useState(null);
  const [savedViewport, setSavedViewport] = useState(() =>
    readLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId)
  );
  const [mapReady, setMapReady] = useState(false);
  const mapApiRef = useRef(null);
  const [controlsWidth, setControlsWidth] = useState(
    () => readRainMapLayout(farmId).controlsWidth
  );
  const [mapHeight, setMapHeight] = useState(
    () => readRainMapLayout(farmId).mapHeight
  );
  const controlsWidthRef = useRef(controlsWidth);
  const mapHeightRef = useRef(mapHeight);

  useEffect(() => {
    controlsWidthRef.current = controlsWidth;
  }, [controlsWidth]);

  useEffect(() => {
    mapHeightRef.current = mapHeight;
  }, [mapHeight]);

  useEffect(() => {
    const layout = readRainMapLayout(farmId);
    controlsWidthRef.current = layout.controlsWidth;
    mapHeightRef.current = layout.mapHeight;
    setControlsWidth(layout.controlsWidth);
    setMapHeight(layout.mapHeight);
    setSavedViewport(readLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId));
    setMapReady(false);
    mapApiRef.current = null;
  }, [farmId]);

  const handleLayoutResize = useCallback((axis, dx, dy) => {
    const maxControlsWidth = Math.min(480, window.innerWidth * 0.42);
    const maxMapHeight = Math.min(920, window.innerHeight * 0.78);

    if (axis === "controls") {
      setControlsWidth((current) => {
        const next = clampSize(current + dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-e") {
      setControlsWidth((current) => {
        const next = clampSize(current - dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-s" || axis === "map-se") {
      setMapHeight((current) => {
        const next = clampSize(current + dy, 360, maxMapHeight);
        mapHeightRef.current = next;
        return next;
      });
    }
    if (axis === "map-se") {
      setControlsWidth((current) => {
        const next = clampSize(current - dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
  }, []);

  function handleMapReady(map) {
    mapApiRef.current = map;
    setMapReady(true);
  }

  function handleSaveMapScreen() {
    if (!mapApiRef.current) {
      toast.error(t("Mapa ainda não está pronto."));
      return;
    }

    const viewport = readViewportFromGoogleMap(mapApiRef.current);
    if (!viewport) {
      toast.error(t("Não foi possível ler a posição atual do mapa."));
      return;
    }

    writeLocalMapViewport(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, viewport);
    writeLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, {
      listWidth: controlsWidthRef.current,
      mapHeight: mapHeightRef.current,
    });
    setSavedViewport(viewport);
    toast.success(t("Tela do mapa salva neste navegador (posição e zoom)."));
  }

  const persistRainMapLayout = useCallback(() => {
    if (!farmId) return;
    writeLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, {
      listWidth: controlsWidthRef.current,
      mapHeight: mapHeightRef.current,
    });
  }, [farmId]);

  const { onResizeStart } = useDragResize(handleLayoutResize, persistRainMapLayout);

  const periodLabel = useMemo(
    () => formatRainPeriodLabel(period, customStart, customEnd, t),
    [period, customStart, customEnd, t]
  );
  const quickRainDate = useMemo(
    () => resolveQuickRainDate(period, customStart, customEnd),
    [period, customStart, customEnd]
  );

  const loadLiveContext = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (period === "custom" && (!customStart || !customEnd)) {
        return;
      }
      if (showSpinner) setLoading(true);
      setLoadError("");
      try {
        const [groupFarms, plots, mapData] = await Promise.all([
          fetchGroupFarms(farmId),
          fetchGroupLandPlots(farmId),
          fetchPluviometryMap(farmId, period, {
            start: customStart,
            end: customEnd,
          }),
        ]);

        const ring = resolvePrimaryBoundaryRing(groupFarms, farmId);
        const nextGauges = buildGaugesFromStations(mapData.stations || []);
        const parentFarm = (groupFarms || []).find(
          (farm) => String(farm.id) === String(farmId)
        );

        setTargetFarmName(
          (parentFarm && parentFarm.nome_da_fazenda) || t("Fazenda")
        );
        setBoundaryRing(ring);
        setLandPlots(plots);
        setGauges(nextGauges);
        setSheetGauge((current) => {
          if (!current) return null;
          const updated = nextGauges.find((gauge) => gauge.id === current.id);
          return updated ? { ...current, ...updated } : current;
        });

        if (!nextGauges.length) {
          setLoadError(
            t(
              "Nenhum pluviômetro sincronizado ainda. Cadastre os pontos no app de campo para aparecerem aqui automaticamente."
            )
          );
        }
      } catch (error) {
        setLoadError(t("Não foi possível carregar os pluviômetros da fazenda."));
        setGauges([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [farmId, period, customStart, customEnd, t]
  );

  useEffect(() => {
    loadLiveContext();
  }, [loadLiveContext]);

  const mapViewport = useMemo(
    () => computeRainMapCenter(boundaryRing, gauges),
    [boundaryRing, gauges]
  );

  function handleSelectPeriod(nextPeriod) {
    if (nextPeriod === "custom" && period !== "custom") {
      const today = toDateInputValue();
      setCustomStart(today);
      setCustomEnd(today);
    }
    setPeriod(nextPeriod);
  }

  function handleSelectGauge(gauge) {
    setSheetGauge(gauge);
  }

  function handleEditRain(gauge) {
    setQuickRain({
      gauge,
      dateStr: quickRainDate || customEnd || toDateInputValue(),
      initialMm: quickRainDate ? gauge.mm : "",
      allowDateChange: !quickRainDate,
    });
  }

  function handleGaugeSaved(updatedGauge) {
    setGauges((current) =>
      current.map((gauge) =>
        gauge.id === updatedGauge.id ? updatedGauge : gauge
      )
    );
    setSheetGauge((current) =>
      current && current.id === updatedGauge.id ? updatedGauge : current
    );
    loadLiveContext({ showSpinner: false });
  }

  function handleDailySaved() {
    loadLiveContext({ showSpinner: false });
  }

  function handleQuickRainSaved() {
    setQuickRain(null);
    toast.success(t("Chuva lançada."));
    loadLiveContext({ showSpinner: false });
  }

  async function handleConfirmDelete() {
    if (!deleteGauge || !deleteGauge.stationUuid) return;
    setDeleting(true);
    try {
      await deletePluviometerStation(farmId, deleteGauge.stationUuid);
      setGauges((current) =>
        current.filter((gauge) => gauge.id !== deleteGauge.id)
      );
      setSheetGauge((current) =>
        current && current.id === deleteGauge.id ? null : current
      );
      setEditGauge((current) =>
        current && current.id === deleteGauge.id ? null : current
      );
      setDeleteGauge(null);
      toast.success(t("Pluviômetro excluído."));
    } catch (error) {
      toast.error(t("Não foi possível excluir o pluviômetro."));
    } finally {
      setDeleting(false);
    }
  }

  function handleGaugeDeleted(deletedGauge) {
    setGauges((current) =>
      current.filter((gauge) => gauge.id !== deletedGauge.id)
    );
    setSheetGauge((current) =>
      current && current.id === deletedGauge.id ? null : current
    );
    setEditGauge(null);
    toast.success(t("Pluviômetro excluído."));
  }

  const totalMm = sumGaugeRainMm(gauges);
  const averageMm = averageGaugeRainMm(gauges);

  return (
    <div className="farm-map-pluviometry">
      <div className="farm-map-pluviometry__intro">
        <div>
          <h2>{t("Mapa de chuva da fazenda")}</h2>
          <p>
            {t(
              "Os pluviômetros cadastrados no app aparecem no mapa. O gestor lança ou corrige os milímetros pela plataforma quando a chuva chega por escrito."
            )}
          </p>
        </div>
        <div className="farm-map-pluviometry__periods">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={
                period === option.id
                  ? "btn btn-primary btn-sm"
                  : "btn btn-default btn-sm"
              }
              onClick={() => handleSelectPeriod(option.id)}
            >
              {t(option.label)}
            </button>
          ))}
          {period === "custom" ? (
            <div className="farm-map-pluviometry__custom-dates">
              <label>
                {t("De")}
                <input
                  type="date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={(event) => setCustomStart(event.target.value)}
                />
              </label>
              <label>
                {t("Até")}
                <input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  onChange={(event) => setCustomEnd(event.target.value)}
                />
              </label>
            </div>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="farm-map-pluviometry__loading">
          <MDSpinner singleColor="#1ABB9C" size={36} />
        </div>
      ) : loadError && gauges.length === 0 ? (
        <div className="farm-map-pluviometry__empty">
          <strong>{loadError}</strong>
        </div>
      ) : (
        <div
          className="farm-map-pluviometry__layout"
          style={{ "--controls-width": `${controlsWidth}px` }}
        >
          <aside className="farm-map-pluviometry__controls">
            <div className="farm-map-pluviometry__summary">
              <div>
                <span>{t("Fazenda")}</span>
                <strong>{targetFarmName}</strong>
              </div>
              <div>
                <span>{t("Período")}</span>
                <strong>{periodLabel}</strong>
              </div>
              <div>
                <span>{t("Média")}</span>
                <strong>{formatRainMm(averageMm)} mm</strong>
              </div>
              <div>
                <span>{t("Total")}</span>
                <strong>{formatRainMm(totalMm)} mm</strong>
              </div>
            </div>

            <p className="farm-map-pluviometry__hint">
              {t(
                "Clique no ponto para abrir a planilha e lançar a chuva do dia. Use o lápis para editar os mm do período e a lixeira para excluir o pluviômetro."
              )}
            </p>

            <ul className="farm-map-pluviometry__gauge-list">
              {gauges.map((gauge) => (
                <li key={gauge.id} className="farm-map-pluviometry__gauge-item">
                  <button
                    type="button"
                    className={
                      sheetGauge && sheetGauge.id === gauge.id
                        ? "farm-map-pluviometry__gauge-button farm-map-pluviometry__gauge-button--active"
                        : "farm-map-pluviometry__gauge-button"
                    }
                    onClick={() => handleSelectGauge(gauge)}
                  >
                    <span>{gauge.label}</span>
                    <strong>{formatRainMm(gauge.mm)} mm</strong>
                    <small>{periodLabel}</small>
                  </button>
                  <div className="farm-map-pluviometry__gauge-actions">
                    <button
                      type="button"
                      className="farm-map-pluviometry__gauge-icon-btn"
                      title={t("Lançar chuva (mm)")}
                      aria-label={t("Lançar chuva (mm)")}
                      onClick={() => handleEditRain(gauge)}
                    >
                      <FaEdit aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="farm-map-pluviometry__gauge-icon-btn farm-map-pluviometry__gauge-icon-btn--danger"
                      title={t("Excluir pluviômetro")}
                      aria-label={t("Excluir pluviômetro")}
                      onClick={() => setDeleteGauge(gauge)}
                    >
                      <FaTrash aria-hidden />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          <div
            className="farm-map-pluviometry__layout-resize"
            title={t("Arraste para redimensionar o mapa")}
            onMouseDown={(event) => onResizeStart("controls", event)}
          />

          <div
            className="farm-map-pluviometry__map-wrap"
            style={{ height: mapHeight }}
          >
            <FarmRainHeatmapMap
              boundaryRing={boundaryRing}
              landPlots={landPlots}
              gauges={gauges}
              center={
                savedViewport
                  ? { lat: savedViewport.lat, lng: savedViewport.lng }
                  : mapViewport.center
              }
              zoom={savedViewport ? savedViewport.zoom : mapViewport.zoom}
              savedViewport={savedViewport}
              mapHeight={mapHeight}
              selectedGaugeId={sheetGauge ? sheetGauge.id : null}
              onGaugeClick={handleSelectGauge}
              onMapReady={handleMapReady}
            />
            <MapViewportToolbar
              onSave={handleSaveMapScreen}
              disabled={!mapReady}
              label={t("Salvar tela")}
              savingLabel={t("Salvando...")}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--e"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-e", event)}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--s"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-s", event)}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--se"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-se", event)}
            />
          </div>
        </div>
      )}

      {sheetGauge ? (
        <PluviometerRainSheet
          farmId={farmId}
          gauge={sheetGauge}
          onEditGauge={setEditGauge}
          onDailySaved={handleDailySaved}
        />
      ) : null}

      {editGauge ? (
        <PluviometerEditModal
          gauge={editGauge}
          farmId={farmId}
          onClose={() => setEditGauge(null)}
          onSaved={handleGaugeSaved}
          onDeleted={handleGaugeDeleted}
        />
      ) : null}

      {quickRain ? (
        <PluviometerDayEditModal
          farmId={farmId}
          stationUuid={quickRain.gauge.stationUuid}
          stationLabel={quickRain.gauge.label}
          dateStr={quickRain.dateStr}
          initialMm={quickRain.initialMm}
          allowDateChange={quickRain.allowDateChange}
          onClose={() => setQuickRain(null)}
          onSaved={handleQuickRainSaved}
        />
      ) : null}

      <FarmMapDeleteConfirmModal
        open={Boolean(deleteGauge)}
        title={t("Excluir pluviômetro")}
        message={t(
          "Tem certeza de que deseja excluir o pluviômetro {{name}}? Ele deixará de aparecer no mapa e no histórico de chuva.",
          { name: (deleteGauge && deleteGauge.label) || t("Pluviômetro") }
        )}
        confirmLabel={t("Excluir pluviômetro")}
        cancelLabel={t("Cancelar")}
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setDeleteGauge(null);
        }}
      />
    </div>
  );
}

export default FarmMapPluviometry;
